/**
 * The pomodoro face: phase, countdown, what comes next, the cycle dots and
 * today's tally. Presentational — the countdown is derived by the caller
 * (`useFocusTimer`) from the server clock, never counted here.
 */
import { clsx } from "clsx";

import type { TimerSnapshot } from "../../api/focus";
import { PHASE_LABEL, isBreak } from "../../lib/focus";
import { formatClock, formatHm, formatMinutes } from "../../lib/focusTime";

export function TimerFace({
  snapshot,
  remainingMs,
  wrappingUp,
}: {
  snapshot: TimerSnapshot;
  remainingMs: number | null;
  wrappingUp: boolean;
}) {
  const { phase, session, settings, cycle, today, then } = snapshot;
  const running = phase !== "idle" && session !== null;
  const totalMs = session ? Date.parse(session.planned_end_at) - Date.parse(session.started_at) : 0;
  const leftMs = remainingMs ?? 0;
  const progress = running && totalMs > 0 ? Math.min(1, Math.max(0, 1 - leftMs / totalMs)) : 0;
  const clock = running
    ? wrappingUp
      ? "0:00"
      : formatClock(Math.ceil(leftMs / 1000))
    : formatClock(settings.focus_minutes * 60);

  const dots = Array.from({ length: cycle.long_break_every }, (_, i) => i < cycle.since_long_break);

  return (
    <div
      className={clsx(
        "timer-face",
        phase === "focus" && "is-focus",
        isBreak(phase) && "is-break",
        wrappingUp && "is-wrapping",
      )}
    >
      <div className="timer-phase">
        <span>{PHASE_LABEL[phase]}</span>
        {session?.goal_title && phase === "focus" ? (
          <span className="timer-goal" title={session.goal_title}>
            · {session.goal_title}
          </span>
        ) : null}
      </div>
      <div className="timer-clock" aria-live="off">
        {clock}
      </div>
      <div className="timer-progress" aria-hidden>
        <span className="timer-progress-fill" style={{ width: `${progress * 100}%` }} />
      </div>
      <div className="timer-sub">
        {wrappingUp ? (
          <span>Wrapping up…</span>
        ) : running && then ? (
          <span>
            {then.kind === "idle"
              ? `then idle · ends ${formatHm(Date.parse(then.starts_at))}`
              : `then ${PHASE_LABEL[then.kind].toLowerCase()} · ${formatHm(Date.parse(then.starts_at))}`}
          </span>
        ) : (
          <span>
            {formatMinutes(settings.focus_minutes)} focus · {formatMinutes(settings.short_break_minutes)} break ·{" "}
            {formatMinutes(settings.long_break_minutes)} after {cycle.long_break_every}
          </span>
        )}
      </div>
      <div className="timer-meta">
        <span className="timer-dots" title={`${cycle.since_long_break} of ${cycle.long_break_every} before the long break`}>
          {dots.map((done, i) => (
            <span key={i} className={clsx("timer-dot", done && "is-done")} />
          ))}
        </span>
        <span>
          today {today.sessions} {today.sessions === 1 ? "session" : "sessions"} · {formatMinutes(today.focus_minutes)}
        </span>
      </div>
      {session?.note ? <div className="timer-note">note · {session.note}</div> : null}
    </div>
  );
}
